import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import MathCaptcha from '@/components/MathCaptcha';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, CheckCircle2, MapPin, Clock, Briefcase, FileText, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ token: '', answer: '', verified: false });
  const [applied, setApplied] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', linkedin_url: '', portfolio_url: '',
    school: '', graduation_year: '', cover_letter: '',
    resume_filename: '', resume_base64: '',
    availability_hours_per_week: 10, referral_source: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/careers/jobs/${slug}`);
        setJob(res.data);
      } catch { navigate('/careers'); }
      finally { setLoading(false); }
    })();
  }, [slug, navigate]);

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) {
      toast.error('File too large (max 5MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || '').split(',')[1] || '';
      setForm(f => ({ ...f, resume_filename: file.name, resume_base64: base64 }));
      toast.success('Resume attached');
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.cover_letter) {
      toast.error('Please complete name, email, and cover letter.');
      return;
    }
    if (!captcha.verified) {
      toast.error('Please solve the quick math check below.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        job_id: job.id,
        graduation_year: form.graduation_year ? Number(form.graduation_year) : null,
        availability_hours_per_week: form.availability_hours_per_week ? Number(form.availability_hours_per_week) : null,
      };
      await axios.post(`${API_URL}/api/careers/applications`, payload);
      setApplied(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail || 'Too many attempts. Please wait a minute.');
      } else {
        toast.error(err.response?.data?.detail || 'Application failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (!job) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/careers" className="flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-600" data-testid="back-to-careers">
            <ArrowLeft className="w-4 h-4" /> All jobs
          </Link>
          <Link to="/" className="font-fredoka text-xl font-bold text-emerald-600">Zazu</Link>
        </div>
      </header>

      {applied && (
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <Card className="border-emerald-300 bg-emerald-50" data-testid="apply-success-card">
            <CardContent className="p-6 flex items-center gap-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 flex-shrink-0" />
              <div>
                <h2 className="font-fredoka text-2xl font-bold text-emerald-800">Application received!</h2>
                <p className="text-emerald-700 text-sm mt-1">Thanks for applying, {form.full_name}. We'll email you at <strong>{form.email}</strong> when the team has reviewed your application. Usually within a week.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-10 grid lg:grid-cols-3 gap-8">
        {/* Job info */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 space-y-8">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge variant="outline">{job.department}</Badge>
              <Badge className="bg-emerald-100 text-emerald-700">Unpaid Internship</Badge>
              <Badge variant="outline" className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location}</Badge>
            </div>
            <h1 className="font-fredoka text-4xl font-bold text-slate-900">{job.title}</h1>
            <p className="text-lg text-slate-600 mt-4">{job.summary}</p>
          </div>

          <Section title="What you&apos;ll do" items={job.responsibilities} />
          <Section title="What we&apos;re looking for" items={job.requirements} />
          <Section title="Skills you&apos;ll use" chips={job.skills} />
          <Section title="What you&apos;ll get" items={job.perks} />
        </motion.div>

        {/* Application form (sticky) */}
        <div>
          <Card className="border-slate-200 sticky top-20" data-testid="apply-form-card">
            <CardContent className="p-5">
              {applied ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
                  <p className="font-semibold text-slate-800">You're in the queue.</p>
                  <Link to="/careers" className="text-emerald-600 text-sm hover:underline">← Back to all jobs</Link>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <h3 className="font-fredoka text-xl font-bold text-slate-800 flex items-center gap-2">
                    <Send className="w-5 h-5 text-emerald-500" /> Apply now
                  </h3>
                  <p className="text-xs text-slate-500 -mt-2">Takes ~5 minutes.</p>

                  <FormField label="Full name*" value={form.full_name} set={v => setForm(f => ({ ...f, full_name: v }))} testid="apply-name" />
                  <FormField label="Email*" type="email" value={form.email} set={v => setForm(f => ({ ...f, email: v }))} testid="apply-email" />
                  <FormField label="Phone" value={form.phone} set={v => setForm(f => ({ ...f, phone: v }))} testid="apply-phone" />
                  <FormField label="LinkedIn URL" value={form.linkedin_url} set={v => setForm(f => ({ ...f, linkedin_url: v }))} testid="apply-linkedin" />
                  <FormField label="Portfolio / GitHub URL" value={form.portfolio_url} set={v => setForm(f => ({ ...f, portfolio_url: v }))} testid="apply-portfolio" />
                  <div className="grid grid-cols-2 gap-2">
                    <FormField label="School" value={form.school} set={v => setForm(f => ({ ...f, school: v }))} testid="apply-school" />
                    <FormField label="Grad year" type="number" value={form.graduation_year} set={v => setForm(f => ({ ...f, graduation_year: v }))} testid="apply-grad-year" />
                  </div>
                  <FormField label="Hours/week available" type="number" value={form.availability_hours_per_week} set={v => setForm(f => ({ ...f, availability_hours_per_week: v }))} testid="apply-hours" />
                  <FormField label="How did you hear about us?" value={form.referral_source} set={v => setForm(f => ({ ...f, referral_source: v }))} testid="apply-referral" />

                  <div>
                    <Label className="text-xs">Cover letter*</Label>
                    <Textarea
                      value={form.cover_letter}
                      onChange={(e) => setForm(f => ({ ...f, cover_letter: e.target.value }))}
                      placeholder="Why this role, why now, what makes you a good fit."
                      rows={5}
                      required
                      data-testid="apply-cover-letter"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Resume (PDF, max 5MB)</Label>
                    <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-emerald-400 hover:text-emerald-600 cursor-pointer transition-all">
                      <FileText className="w-4 h-4" />
                      <span className="truncate flex-1">{form.resume_filename || 'Click to attach'}</span>
                      <input
                        type="file"
                        accept=".pdf,.docx,.doc"
                        className="hidden"
                        onChange={handleResumeUpload}
                        data-testid="apply-resume-input"
                      />
                    </label>
                  </div>

                  <MathCaptcha onChange={setCaptcha} />

                  <Button type="submit" disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="apply-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit application
                  </Button>
                  <p className="text-[11px] text-slate-400 text-center">
                    By applying you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

const Section = ({ title, items, chips }) => (
  <section>
    <h2 className="font-fredoka text-xl font-bold text-slate-800 mb-3">{title}</h2>
    {items && (
      <ul className="space-y-2 text-slate-600">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2"><span className="text-emerald-500 mt-1">▸</span><span>{it}</span></li>
        ))}
      </ul>
    )}
    {chips && (
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => <Badge key={c} variant="outline" className="bg-slate-50">{c}</Badge>)}
      </div>
    )}
  </section>
);

const FormField = ({ label, value, set, type = 'text', testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value} onChange={(e) => set(e.target.value)} data-testid={testid} />
  </div>
);

export default JobDetailPage;
